import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Box, Typography, Button, IconButton, CircularProgress, alpha } from '@mui/material';
import LockClockIcon from '@mui/icons-material/LockClock';
import CloseIcon from '@mui/icons-material/Close';
import { ICaixaAbertura, IFormaPagamentoAbertura } from '../../shared/services/api/Venda/CaixaTurnoService';
import { ISaldoPreviewCaixa, SessaoCaixaService } from '../../shared/services/api/Venda/SessaoCaixaService';
import { VFieldMoney2FaturamentoPercentual } from './VFieldMoney2FaturamentoPercentual';
import { PDVThemeColors } from './PDVThemes';
import { PDFAberturaCaixa } from './impressaocaixa';

interface DialogAbrirCaixaProps {
  // Agora é uma dialog normal (aberta pelo F2 quando o caixa está fechado),
  // não mais uma tela sempre montada — ver comentário em Pdv.tsx sobre a
  // tela "travada".
  open: boolean;
  onClose: () => void;
  idconta: string;
  nomeOperador: string;
  colors: PDVThemeColors;
  onAberto: (abertura: ICaixaAbertura) => void;
  onImprimir: (blob: Blob, titulo: string) => void;
}

// Dialog normal por cima da tela do PDV (não fullScreen) — igual ao
// DialogSelecionarCaixa reaberto pelo F2, pra não trocar a tela toda por um
// fundo branco só pra pedir o valor de abertura.
const Shell: React.FC<{ open: boolean; onClose: () => void; children: React.ReactNode }> = ({ open, onClose, children }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    // Sem fechar pelo Escape: evita que o mesmo Escape que fecharia essa
    // dialog dispare também o atalho global de Sair (ver handleKeyPress em
    // Pdv.tsx) — fechar aqui é só pelo X (ou clicando fora) mesmo.
    disableEscapeKeyDown
    PaperProps={{ sx: { borderRadius: '16px' } }}
  >
    <Box sx={{ p: 4, textAlign: 'center', position: 'relative' }}>
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'text.secondary',
          '&:hover': { backgroundColor: alpha('#000', 0.06) },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      {children}
    </Box>
  </Dialog>
);

const formatarReal = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const IconBadge: React.FC<{ colors: PDVThemeColors; icon: React.ReactNode }> = ({ colors, icon }) => (
  <Box
    sx={{
      width: 64,
      height: 64,
      borderRadius: '18px',
      mx: 'auto',
      mb: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: colors.primaryGradient,
    }}
  >
    {icon}
  </Box>
);

// Aberta sob demanda (F2, ou pelo rodapé) quando o operador não tem sessão
// de caixa ativa. A tela do PDV atrás dela já fica visível (só travada — ver
// Pdv.tsx), diferente de antes, quando essa dialog ficava sempre montada e
// era a única coisa na tela enquanto o caixa estivesse fechado.
//
// Do ponto de vista do operador, "abrir sessão" é a única ação — não existe
// mais um passo separado de "abrir o caixa" antes: SessaoCaixaService.abrir
// já abre o turno por baixo se ele não estiver aberto (ver abrirSessao em
// SessaoCaixa.ts), então essa dialog nunca precisa saber se o turno estava
// aberto ou não.
export const DialogAbrirCaixa: React.FC<DialogAbrirCaixaProps> = ({
  open, onClose, idconta, nomeOperador, colors, onAberto, onImprimir,
}) => {
  const [valorDinheiro, setValorDinheiro] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [saldoPreview, setSaldoPreview] = useState<ISaldoPreviewCaixa | null>(null);
  const [carregandoSaldo, setCarregandoSaldo] = useState(false);
  const valorRef = useRef<HTMLInputElement>(null);

  // Busca o saldo real já existente nesse caixa (ex.: troco que sobrou de um
  // turno anterior nunca fechado) toda vez que a dialog abre, só pro
  // operador conferir contra o que tem fisicamente na gaveta antes de
  // confirmar o valor de abertura — NÃO pré-preenche o campo com esse valor:
  // o saldo no banco pode não existir de verdade na gaveta, então o campo
  // sempre começa zerado e quem abre digita o que realmente contou.
  useEffect(() => {
    if (!open) return;
    setErro('');
    setValorDinheiro(0);
    setSaldoPreview(null);
    setCarregandoSaldo(true);
    SessaoCaixaService.getSaldoPreview(idconta).then((resultado) => {
      setCarregandoSaldo(false);
      if (resultado instanceof Error) return;
      setSaldoPreview(resultado);
    });
  }, [open, idconta]);

  const abrirCaixa = async () => {
    // Tolerância pra ruído de ponto flutuante (mesmo critério usado no
    // backend, ex. SessaoCaixa.ts) — sem ela, um caixa de fato zerado podia
    // cair aqui como "negativo" por causa de um resto tipo -0.0000000001.
    if (valorDinheiro < -0.004) {
      setErro('O valor de abertura não pode ser negativo');
      return;
    }
    setSalvando(true);
    setErro('');

    // Só Dinheiro — mesma regra do fechamento (fecharSessaoComConferencia):
    // PIX/Cartão não ficam fisicamente no caixa, roteiam direto pra
    // recebível na hora da venda, então não fazem sentido como valor de
    // abertura. Abrir com R$ 0 (sem fundo de troco) é permitido.
    const formasPagamento: IFormaPagamentoAbertura[] = [{ categoria: 'Dinheiro', valor: valorDinheiro }];

    // Uma chamada só: abrirSessao (backend) já abre o turno por baixo se
    // ele não estiver aberto (mesmo valor informado aqui), ou só anexa a
    // sessão se já estiver (ver SessaoCaixa.ts) — pro operador, "abrir
    // sessão" e "abrir caixa" são a mesma ação, sempre. Sem a chamada
    // separada a CaixaTurnoService.abrir de antes, não existe mais o estado
    // intermediário de "caixa abriu mas a sessão falhou".
    const sessao = await SessaoCaixaService.abrir(idconta, formasPagamento);
    if (sessao instanceof Error) {
      setErro(sessao.message);
      setSalvando(false);
      return;
    }

    const abertura: ICaixaAbertura = {
      idconta: sessao.idconta,
      dataabertura: sessao.dataabertura,
      valorabertura: sessao.valorabertura ?? 0,
    };

    try {
      const blob = await PDFAberturaCaixa({
        operador: nomeOperador,
        dataabertura: abertura.dataabertura,
        // Já é o total com que o caixa abriu (o que já estava nele + o
        // acrescentado agora — ver abrirSessao no backend).
        valorabertura: abertura.valorabertura,
      });
      onImprimir(blob, 'Abertura de caixa');
    } catch {
      // Se a impressão falhar, a sessão já foi aberta — não trava o operador por isso.
    }

    setSalvando(false);
    onAberto(abertura);
  };

  return (
    <Shell open={open} onClose={onClose}>
      <IconBadge colors={colors} icon={<LockClockIcon sx={{ fontSize: 32, color: colors.textOnPrimary }} />} />

      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Abrir sessão
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        O que já está no caixa continua nele. Informe só o dinheiro que vai acrescentar (pode ser 0).
      </Typography>

      {carregandoSaldo && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, mb: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!carregandoSaldo && saldoPreview !== null && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: '12px',
            backgroundColor: alpha('#000', 0.04),
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Saldo já existente no caixa (confira na gaveta)
          </Typography>
          <Typography variant="subtitle1" fontWeight={700}>
            {formatarReal(saldoPreview.saldototal)}
          </Typography>
        </Box>
      )}

      <VFieldMoney2FaturamentoPercentual
        id="valorabertura"
        label="Valor a acrescentar em Dinheiro (R$)"
        value={valorDinheiro}
        valor={(_id, novoValor) => setValorDinheiro(Number(novoValor) || 0)}
        inputRef={valorRef}
        fullWidth
        autoFocus
        onKeyDown={(e) => { if (e.key === 'Enter') abrirCaixa(); }}
      />

      {/* Com quanto o caixa vai abrir de fato: o valor informado vem do
          Cofre e SOMA ao que já está no caixa (ver abrirSessaoTransferencia
          no backend) — a pedido, o operador vê esse total antes de abrir. */}
      {!carregandoSaldo && saldoPreview !== null && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: '12px',
            bgcolor: alpha(colors.primarySolid, 0.08),
            border: `1px solid ${alpha(colors.primarySolid, 0.3)}`,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            O caixa vai abrir com
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {formatarReal(saldoPreview.saldototal + valorDinheiro)}
          </Typography>
          {valorDinheiro > 0 && (
            <Typography variant="caption" color="text.secondary">
              {formatarReal(saldoPreview.saldototal)} já no caixa + {formatarReal(valorDinheiro)} do Cofre
            </Typography>
          )}
        </Box>
      )}

      {erro && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          {erro}
        </Typography>
      )}

      <Button
        fullWidth
        variant="contained"
        disabled={salvando}
        onClick={abrirCaixa}
        sx={{
          mt: 3,
          py: 1.3,
          borderRadius: '12px',
          fontWeight: 700,
          background: colors.primaryGradient,
          color: colors.textOnPrimary,
          '&:hover': { background: colors.hoverGradient },
        }}
      >
        {salvando ? 'Abrindo...' : 'Abrir sessão'}
      </Button>
    </Shell>
  );
};
